import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  RefreshCw,
  UserRound,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { API } from "@/lib/booking";
import { SITE_CONFIG } from "@/lib/siteConfig";

const AVAILABILITY_DAYS = 60;

// Order follows the API: the booking hold (and therefore the OTP) is only
// created once the patient's details have been submitted, so Details now
// precedes Verification.
const STEP_META = [
  { id: 1, title: "Appointment" },
  { id: 2, title: "Details" },
  { id: 3, title: "Verification" },
  { id: 4, title: "Confirmation" },
];

// Convert a local JS Date to a YYYY-MM-DD string (avoids UTC-shift issues)
function toLocalISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Slots carry ISO-8601 instants (startsAt / endsAt) only. Display them in the
// visitor's local time — the raw startsAt value is what gets submitted later.
function formatSlotTime(startsAt) {
  if (!startsAt) return "";
  const parsed = new Date(startsAt);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatLongDate(date) {
  if (!date) return "Not selected";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

export default function Book() {
  // ── Services from API ──────────────────────────────────────────────────────
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [servicesError, setServicesError] = useState("");

  // ── Provider from API ─────────────────────────────────────────────────────
  // Availability and bookings both require a providerId, and /public/providers
  // is the only public way to learn one. All brands have exactly one active
  // provider, so the first provider returned is used as the default.
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");

  // ── Selected service (by API id) ──────────────────────────────────────────
  const [selectedServiceId, setSelectedServiceId] = useState("");

  // ── Availability from API ─────────────────────────────────────────────────
  // Shape: { days: [{ date: "YYYY-MM-DD", slots: [{ time, providerId, providerName, durationMinutes }] }] }
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");

  // ── Selected date + slot ──────────────────────────────────────────────────
  const [date, setDate] = useState(undefined);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // ── Booking flow ──────────────────────────────────────────────────────────
  // 1 Appointment -> 2 Details -> 3 Verification (OTP) -> confirmed screen.
  // The API creates the hold *and* issues the OTP in one call, so the patient's
  // details are collected before the code is entered.
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Live hold returned by POST /public/bookings — its OTP confirms it.
  const [hold, setHold] = useState(null); // { bookingId, reference, holdExpiresAt, phone }
  const [confirmation, setConfirmation] = useState(null); // { reference }
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // ── Static brand config ───────────────────────────────────────────────────
  const audienceConfig = SITE_CONFIG.audiences?.[0] || {};
  const accent = audienceConfig.accent || "#C8A86B";
  const accentBg = audienceConfig.accent_bg || "#201914";

  // ── Derived values ────────────────────────────────────────────────────────
  const selectedServiceObj = useMemo(
    () => services.find((s) => s.id === selectedServiceId) || null,
    [services, selectedServiceId]
  );
  // Single provider per brand: its name is shown alongside slots and in the summary.
  const providerName =
    providers.find((p) => p.id === providerId)?.name || "";

  // Slots for the currently selected date
  const todaySlots = useMemo(() => {
    if (!availability || !date) return [];
    const key = toLocalISO(date);
    const day = availability.days?.find((d) => d.date === key);
    return day?.slots || [];
  }, [availability, date]);

  // Slot count map for calendar decorators
  const availabilityMap = useMemo(() => {
    const map = {};
    for (const day of availability?.days ?? []) {
      map[day.date] = day.slots?.length ?? 0;
    }
    return map;
  }, [availability]);

  // ── Fetch services + providers on mount ───────────────────────────────────
  useEffect(() => {
    let active = true;
    setLoadingServices(true);
    setServicesError("");

    const getJson = (url) =>
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });

    Promise.all([
      getJson(`${API}/public/services`),
      getJson(`${API}/public/providers`),
    ])
      .then(([servicesData, providersData]) => {
        if (!active) return;
        const serviceList = Array.isArray(servicesData)
          ? servicesData
          : servicesData.services ?? [];
        const providerList = providersData?.providers ?? [];
        setServices(serviceList);
        setProviders(providerList);
        setProviderId(providerList[0]?.id ?? "");
      })
      .catch(() => {
        if (active) {
          setServicesError(
            "Unable to load appointment options. Please try again."
          );
        }
      })
      .finally(() => {
        if (active) setLoadingServices(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // ── Fetch availability whenever service or provider is known ─────────────
    useEffect(() => {
      if (!selectedServiceId || !providerId) return;

      let active = true;
      setLoadingAvailability(true);
      setAvailabilityError("");
      setDate(undefined);
      setSelectedSlot(null);

      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + AVAILABILITY_DAYS);

      const params = new URLSearchParams({
        providerId,
        serviceId: selectedServiceId,
        from: toLocalISO(today),
        to: toLocalISO(endDate),
      });

      fetch(`${API}/public/availability?${params}`)
        .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
        .then((data) => {
        if (active) setAvailability(data);
      })
        .catch(() => {
        if (active) {
          setAvailabilityError(
            "Unable to load availability. Please try again."
          );
        }
      })
        .finally(() => {
        if (active) setLoadingAvailability(false);
      });

    return () => {
      active = false;
    };
  }, [selectedServiceId, providerId]);

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const isDayAvailable = useCallback(
    (d) => {
      if (!availability) return false;
      return (availabilityMap[toLocalISO(d)] ?? 0) > 0;
    },
    [availability, availabilityMap]
  );

  const isDayDisabled = useCallback(
    (d) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (d < today) return true;
      if (!availability) return true;
      return !isDayAvailable(d);
    },
    [availability, isDayAvailable]
  );

  // Custom DayContent: renders day number + accent dot when slots exist
  const DayContent = useCallback(
    ({ date: dayDate, displayMonth }) => {
      const key = toLocalISO(dayDate);
      const count = availabilityMap[key] ?? 0;
      const isCurrentMonth =
        dayDate.getMonth() === displayMonth.getMonth();
      return (
        <span className="relative flex flex-col items-center leading-none">
          <span>{dayDate.getDate()}</span>
          {isCurrentMonth && count > 0 && (
            <span
              className="absolute -bottom-1 h-1 w-1 rounded-full"
              style={{ background: accent }}
              aria-hidden="true"
            />
          )}
        </span>
      );
    },
    [availabilityMap, accent]
  );

  // ── Refetch availability (used after 409 conflict) ────────────────────────
  const refetchAvailability = useCallback(() => {
    if (!selectedServiceId || !providerId) return;
    setLoadingAvailability(true);
    setAvailabilityError("");

    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + AVAILABILITY_DAYS);

    const params = new URLSearchParams({
      providerId,
      serviceId: selectedServiceId,
      from: toLocalISO(today),
      to: toLocalISO(endDate),
    });

    fetch(`${API}/public/availability?${params}`)
      .then((r) => r.json())
      .then((data) => setAvailability(data))
      .catch(() => setAvailabilityError("Unable to refresh availability."))
      .finally(() => setLoadingAvailability(false));
  }, [selectedServiceId, providerId]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep = (currentStep) => {
    const nextErrors = {};

    if (currentStep === 1) {
      if (!selectedServiceId) {
        nextErrors.service = "Select an appointment option.";
      } else if (!date) {
        nextErrors.date = "Please select an available date.";
      } else if (!selectedSlot) {
        nextErrors.slot = "Please select a time slot.";
      }
    }

    if (currentStep === 2) {
      if (!firstName.trim()) nextErrors.firstName = "First name is required.";
      if (!lastName.trim()) nextErrors.lastName = "Last name is required.";
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
        nextErrors.email = "Valid email required.";
      }
      if (!phone.trim() || phone.trim().length < 7) {
        nextErrors.phone = "Enter a valid phone number.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ── Create the hold (this call is also what sends the OTP) ────────────────
  const createHold = async () => {
    if (!validateStep(2)) return;

    // A hold is already live for this slot. Posting again would collide with
    // our own reservation (the API refuses overlapping holds with a 409), so
    // go straight back to the code step with the hold we already have.
    if (hold?.bookingId) {
      setOtpInput("");
      setOtpError("");
      setStep(3);
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        providerId,
        serviceId: selectedServiceId,
        // The slot's startsAt goes to the API verbatim: the server owns the
        // interpretation of the instant, so there is no timezone maths here.
        startsAt: selectedSlot?.startsAt,
        patient: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
      };

      const response = await fetch(`${API}/public/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // 409 — the slot was taken between picking it and submitting.
      if (response.status === 409) {
        setSelectedSlot(null);
        setDate(undefined);
        refetchAvailability();
        setStep(1);
        setErrors({
          conflict: "That slot was just booked — please pick another time.",
        });
        return;
      }

      // 400 — validation errors from the backend.
      if (response.status === 400) {
        const errorBody = await response.json().catch(() => ({}));
        const fieldErrors = errorBody.errors || errorBody.fieldErrors || {};
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          setErrors({
            submit:
              errorBody.error ||
              errorBody.detail ||
              errorBody.message ||
              "Invalid booking details. Please check and try again.",
          });
        }
        return;
      }

      // 403 — the bot check refused the request.
      if (response.status === 403) {
        setErrors({
          submit:
            "We could not verify this request. Please refresh the page and try again.",
        });
        return;
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody.error || errorBody.detail || "Unable to create booking."
        );
      }

      const data = await response.json();
      setHold({
        bookingId: data.bookingId,
        reference: data.reference,
        holdExpiresAt: data.holdExpiresAt,
        // The code goes to the number on the booking, so show that number.
        phone: phone.trim(),
      });
      setOtpInput("");
      setOtpError("");
      setStep(3);
    } catch (error) {
      setErrors({
        submit:
          error?.message ||
          "We could not reach the booking service. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Confirm the hold with the 6-digit code ────────────────────────────────
  const confirmOtp = async () => {
    if (!hold?.bookingId) return;
    if (!/^\d{6}$/.test(otpInput)) {
      setOtpError("Enter the 6-digit code");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");
    try {
      const response = await fetch(
        `${API}/public/bookings/${hold.bookingId}/confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: otpInput }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success !== false) {
        setConfirmation({ reference: hold.reference });
        return;
      }

      // 429 — too many wrong codes; the API has released the hold.
      if (response.status === 429) {
        setOtpError(
          data.error ||
            "Too many incorrect codes, so this reservation has been released. Please start a new booking."
        );
        return;
      }

      // 409/410 — the hold is no longer awaiting confirmation, or it expired.
      if (response.status === 409 || response.status === 410) {
        setHold(null);
        setOtpInput("");
        setOtpError("");
        setSelectedSlot(null);
        setDate(undefined);
        refetchAvailability();
        setStep(1);
        setErrors({
          conflict:
            "Your reservation expired before the code was confirmed, so that time is no longer held. Please pick another time.",
        });
        return;
      }

      // 400 — the code was wrong or malformed.
      if (response.status === 400) {
        setOtpError(
          data.error || "That code is not correct. Please check it and try again."
        );
        return;
      }

      // 404 — the hold is gone entirely; start again.
      if (response.status === 404) {
        setHold(null);
        setOtpInput("");
        setOtpError("");
        setStep(1);
        setErrors({
          conflict:
            "We could not find this booking any more. Please choose a time again.",
        });
        return;
      }

      setOtpError(data.error || "Unable to verify the code. Please try again.");
    } catch {
      setOtpError(
        "We could not reach the booking service. Please check your connection and try again."
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleOtpChange = (event, index) => {
    const value = event.target.value.replace(/\D/g, "");
    const nextValue = otpInput.split("");
    nextValue[index] = value;
    setOtpInput(nextValue.join("").slice(0, 6));
    if (value && index < 5) otpRefs[index + 1].current?.focus();
    if (!value && index > 0) otpRefs[index - 1].current?.focus();
    setOtpError("");
  };

  // ── Reset page state ──────────────────────────────────────────────────────
  const resetPageState = () => {
    setConfirmation(null);
    setHold(null);
    setStep(1);
    setSelectedServiceId("");
    setAvailability(null);
    setDate(undefined);
    setSelectedSlot(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setOtpInput("");
    setOtpError("");
    setErrors({});
  };

  // ── Input style helper ────────────────────────────────────────────────────
  const inputStyle = (field) => ({
    width: "100%",
    padding: "13px 15px",
    fontFamily: "Manrope, sans-serif",
    fontSize: "14px",
    background: "#FCFBF9",
    color: "#1C1917",
    outline: "none",
    borderRadius: "16px",
    border: `1px solid ${errors[field] ? "#ef4444" : "#E7E5E4"}`,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  });

  // ══════════════════════════════════════════════════════════════════════════
  // Booking confirmed screen — the booking is live at this point
  // ══════════════════════════════════════════════════════════════════════════
  if (confirmation) {
    return (
      <section className="section-spacing pt-8 md:pt-10">
        <div className="container-luxury">
          <div className="mx-auto max-w-2xl rounded-[2rem] border border-stone-200 bg-white p-7 text-center shadow-[0_20px_50px_rgba(41,37,36,0.08)] md:p-10">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                background: accentBg,
                color: accent,
              }}
            >
              <CheckCircle2 size={28} />
            </div>

            <p
              className="mt-5 font-accent text-[11px] uppercase tracking-[0.24em]"
              style={{ color: accent }}
            >
              {SITE_CONFIG.display_name || "Booking"}
            </p>
            <h1 className="mt-3 font-heading text-3xl text-charcoal md:text-4xl">
              Appointment confirmed
            </h1>
            <p className="mx-auto mt-4 max-w-xl font-body text-base leading-relaxed text-stone-600">
              Your appointment is booked. Keep the reference below — the clinic
              will ask for it if you need to change anything.
            </p>

            {confirmation.reference && (
              <div className="mx-auto mt-7 max-w-sm rounded-2xl border border-stone-200 bg-stone-50 px-6 py-4">
                <p className="font-accent text-[11px] uppercase tracking-[0.24em] text-stone-500">
                  Reference
                </p>
                <p className="mt-2 font-heading text-2xl text-charcoal">
                  {confirmation.reference}
                </p>
              </div>
            )}

            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={resetPageState}
                className="inline-flex h-12 items-center justify-center rounded-full border border-stone-300 px-6 font-body text-sm font-medium text-charcoal transition-colors hover:bg-stone-50"
              >
                Start another booking
              </button>
              <Link
                to="/contact"
                className="inline-flex h-12 items-center justify-center rounded-full px-6 font-body text-sm font-medium text-warm-white"
                style={{ background: accentBg }}
              >
                Contact the clinic
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Services loading screen
  // ══════════════════════════════════════════════════════════════════════════
  if (loadingServices) {
    return (
      <section className="section-spacing pt-8 md:pt-10">
        <div className="container-luxury">
          <div className="mx-auto max-w-2xl rounded-[2rem] border border-stone-200 bg-white p-8 text-center shadow-[0_20px_50px_rgba(41,37,36,0.08)]">
            <p className="font-accent text-[11px] uppercase tracking-[0.24em] text-accent-gold">
              Booking
            </p>
            <h1 className="mt-3 font-heading text-3xl text-charcoal md:text-4xl">
              Preparing your appointment options&hellip;
            </h1>
          </div>
        </div>
      </section>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Services error screen — no silent fallback to mock data
  // ══════════════════════════════════════════════════════════════════════════
  if (servicesError) {
    return (
      <section className="section-spacing pt-8 md:pt-10">
        <div className="container-luxury">
          <div className="mx-auto max-w-2xl rounded-[2rem] border border-stone-200 bg-white p-8 text-center shadow-[0_20px_50px_rgba(41,37,36,0.08)]">
            <p
              className="font-accent text-[11px] uppercase tracking-[0.24em]"
              style={{ color: accent }}
            >
              Unavailable
            </p>
            <h1 className="mt-3 font-heading text-3xl text-charcoal md:text-4xl">
              We could not load the booking page
            </h1>
            <p className="mx-auto mt-4 max-w-xl font-body text-base leading-relaxed text-stone-600">
              {servicesError}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-full border border-stone-300 px-6 font-body text-sm font-medium text-charcoal transition-colors hover:bg-stone-50"
            >
              <RefreshCw size={15} /> Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Main booking form
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div data-testid="booking-page">
      {/* Header banner */}
      <section className="border-b border-stone-200 bg-[linear-gradient(180deg,#f8f5ef_0%,#ffffff_100%)] py-7 md:py-9">
        <div className="container-luxury">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p
                className="font-accent text-[11px] uppercase tracking-[0.24em]"
                style={{ color: accent }}
              >
                Specialist Dental London
              </p>
              <h1 className="mt-3 font-heading text-[2.1rem] leading-[1.02] tracking-tight text-charcoal md:text-[3rem]">
                Book an appointment
              </h1>
              <p className="mt-3 max-w-2xl font-body text-[0.98rem] leading-relaxed text-stone-600 md:text-lg">
                Choose the right consultation, select a preferred date, and
                secure your appointment in a few simple steps.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {STEP_META.map((item) => (
                <div
                  key={item.id}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-2"
                  style={{
                    borderColor: step >= item.id ? accent : "#E7E5E4",
                    background: step === item.id ? "#ffffff" : "#FAFAF9",
                  }}
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full font-body text-xs font-semibold"
                    style={{
                      background: step >= item.id ? accentBg : "#F1F0EE",
                      color: step >= item.id ? accent : "#78716C",
                    }}
                  >
                    {item.id}
                  </span>
                  <span className="font-body text-xs font-medium text-charcoal">
                    {item.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form body */}
      <section className="pb-14 pt-5 md:pb-20 md:pt-8">
        <div className="container-luxury">
          <div className="grid gap-5 lg:grid-cols-[1.18fr_0.82fr] lg:gap-8">
            <div className="rounded-[1.85rem] border border-stone-200 bg-white p-4 shadow-[0_20px_50px_rgba(41,37,36,0.06)] sm:p-6 md:p-8">

              {/* ── STEP 1: Appointment ─────────────────────────────────── */}
              {step === 1 && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  className="space-y-8"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p
                        className="font-accent text-[11px] uppercase tracking-[0.24em]"
                        style={{ color: accent }}
                      >
                        Step 1
                      </p>
                      <h2 className="mt-2 font-heading text-2xl text-charcoal md:text-3xl">
                        Choose your appointment
                      </h2>
                    </div>
                  </div>

                  {/* 409 conflict banner */}
                  {errors.conflict && (
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[1.25rem] border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="font-body text-sm text-amber-800">
                        {errors.conflict}
                      </p>
                    </motion.div>
                  )}

                  {/* Progressive UI Flow: Service Picker -> Date & Time */}
                  <AnimatePresence mode="wait">
                    {!selectedServiceId ? (
                      <motion.div
                        key="service-picker"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <label className="mb-4 block font-body text-sm font-medium text-charcoal">
                          Select your appointment type
                        </label>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {services.map((svc) => (
                            <motion.button
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.98 }}
                              key={svc.id}
                              type="button"
                              onClick={() => { setSelectedServiceId(svc.id); setErrors({}); }}
                              className="text-left relative overflow-hidden rounded-[1.25rem] border p-5 transition-all duration-300 flex flex-col justify-between min-h-[130px] bg-[#FCFBF9] border-[#E7E5E4] hover:border-[#C8A86B] hover:shadow-[0_10px_30px_-10px_rgba(200,168,107,0.25)] group"
                            >
                              <div className="flex justify-between items-start gap-4">
                                <h3 className="font-heading text-lg leading-tight pr-4 text-stone-900 group-hover:text-stone-900">
                                  {svc.name}
                                </h3>
                                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent transition-colors group-hover:border-[#C8A86B]"></div>
                              </div>
                              
                              <div className="mt-4">
                                {(svc.durationMin || svc.depositPence > 0) && (
                                  <div className="flex flex-wrap gap-2 text-[11px] font-body uppercase tracking-widest font-semibold text-stone-500">
                                    {svc.durationMin && <span className="flex items-center gap-1.5"><Clock size={12}/> {svc.durationMin} min</span>}
                                    {svc.depositPence > 0 && <span>£{(svc.depositPence / 100).toFixed(2)} deposit</span>}
                                  </div>
                                )}
                                {svc.shortDescription && (
                                  <p className="mt-2 text-sm font-body leading-relaxed text-stone-500 opacity-90 line-clamp-2">
                                    {svc.shortDescription}
                                  </p>
                                )}
                              </div>
                            </motion.button>
                          ))}
                        </div>
                        {errors.service && (
                          <p className="mt-3 font-body text-sm text-red-500">
                            {errors.service}
                          </p>
                        )}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="calendar-picker"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {/* Collapsed Service Header */}
                        <div className="rounded-[1.25rem] border border-[#C8A86B] bg-[#201914] p-4 flex items-center justify-between shadow-[0_8px_30px_rgba(32,25,20,0.12)]">
                          <div className="flex items-center gap-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C8A86B]/20">
                              <Check size={16} className="text-[#C8A86B]" />
                            </div>
                            <div>
                              <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-[#C8A86B]">
                                Selected Service
                              </p>
                              <h3 className="font-heading text-[1.1rem] text-white mt-0.5">
                                {selectedServiceObj?.name}
                              </h3>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setSelectedServiceId(''); setDate(undefined); setSelectedSlot(null); }}
                            className="px-4 py-2 rounded-full border border-[#C8A86B]/30 text-[#C8A86B] text-xs font-semibold hover:bg-[#C8A86B]/10 transition-colors"
                          >
                            Change
                          </button>
                        </div>

                        {/* Date + Time Layout */}
                        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                          
                          {/* Date picker */}
                          <div className="rounded-[1.5rem] border border-stone-200 bg-[#FCFBF9] p-5 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
                            <div className="mb-5 flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-stone-100 shadow-sm">
                                <CalendarDays size={18} style={{ color: accent }} />
                              </div>
                              <div>
                                <p className="font-body text-sm font-semibold text-charcoal">
                                  Select a date
                                </p>
                                <p className="font-body text-xs text-stone-500">
                                  {loadingAvailability
                                    ? "Loading availability\u2026"
                                    : "Highlighted dates have available slots"}
                                </p>
                              </div>
                            </div>

                            {loadingAvailability ? (
                              <div className="flex h-[320px] items-center justify-center rounded-[1.2rem] border border-stone-100 bg-white">
                                <p className="font-body text-sm text-stone-400 animate-pulse">
                                  Loading calendar&hellip;
                                </p>
                              </div>
                            ) : availabilityError ? (
                              <div className="rounded-[1.2rem] border border-stone-100 bg-white p-6 text-center">
                                <p className="font-body text-sm text-stone-500">
                                  {availabilityError}
                                </p>
                                <button
                                  type="button"
                                  onClick={refetchAvailability}
                                  className="mt-4 inline-flex h-9 items-center gap-2 rounded-full border border-stone-300 px-5 font-body text-xs font-medium text-charcoal hover:bg-stone-50"
                                >
                                  <RefreshCw size={13} /> Retry
                                </button>
                              </div>
                            ) : (
                              <div className="overflow-hidden rounded-[1.2rem] border border-stone-100 bg-white p-3 shadow-sm flex justify-center">
                                <Calendar
                                  mode="single"
                                  selected={date}
                                  onSelect={(d) => {
                                    setDate(d);
                                    setSelectedSlot(null);
                                    setErrors((e) => ({ ...e, date: "", slot: "" }));
                                  }}
                                  disabled={isDayDisabled}
                                  components={{ DayContent }}
                                />
                              </div>
                            )}

                            {errors.date && (
                              <p className="mt-3 font-body text-sm text-red-500">
                                {errors.date}
                              </p>
                            )}
                          </div>

                          {/* Time slot picker */}
                          <div className="rounded-[1.5rem] border border-stone-200 bg-[#FCFBF9] p-5 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)] flex flex-col h-[450px]">
                            <div className="mb-5 shrink-0">
                              <p className="font-body text-sm font-semibold text-charcoal">
                                Available times
                              </p>
                              <p className="mt-1 font-body text-xs text-stone-500">
                                {!date
                                  ? "Select a date to view available appointment times."
                                  : todaySlots.length === 0
                                  ? "No slots available for this date."
                                  : `${todaySlots.length} slot${todaySlots.length !== 1 ? "s" : ""} available`}
                              </p>
                            </div>

                            {date && todaySlots.length > 0 ? (
                              <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                                <div className="grid grid-cols-2 gap-3 pb-2">
                                  <AnimatePresence>
                                    {todaySlots.map((slot, idx) => {
                                      const isSelected = selectedSlot?.startsAt === slot.startsAt;
                                      return (
                                        <motion.button
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: idx * 0.02 }}
                                          key={slot.startsAt}
                                          type="button"
                                          onClick={() => {
                                            setSelectedSlot(slot);
                                            setErrors((e) => ({ ...e, slot: "" }));
                                          }}
                                          className="flex flex-col items-center justify-center rounded-xl border p-3 text-center transition-all duration-200"
                                          style={{
                                            borderColor: isSelected ? accent : "#E7E5E4",
                                            background: isSelected ? accentBg : "#FFFFFF",
                                            boxShadow: isSelected ? `0 4px 12px -4px ${accent}50` : "none"
                                          }}
                                        >
                                          <span className="font-body text-sm font-semibold tracking-wide" style={{ color: isSelected ? accent : "#1C1917" }}>
                                            {formatSlotTime(slot.startsAt)}
                                          </span>
                                          <span className="mt-1 font-body text-[10px] uppercase tracking-wider line-clamp-1 opacity-80" style={{ color: isSelected ? accent : "#78716C" }}>
                                            {providerName}
                                          </span>
                                        </motion.button>
                                      );
                                    })}
                                  </AnimatePresence>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-1 flex-col items-center justify-center rounded-[1.2rem] border border-dashed border-stone-200 bg-white p-6 text-center">
                                <Clock size={24} className="text-stone-300 mb-3" />
                                <p className="font-body text-sm text-stone-500 max-w-[200px]">
                                  {date
                                    ? "No appointment slots are available for this date. Please choose another day."
                                    : "The time options will appear here once a date is selected."}
                                </p>
                              </div>
                            )}

                            {errors.slot && (
                              <p className="mt-auto pt-4 font-body text-sm text-red-500 shrink-0">
                                {errors.slot}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}

              {/* ── STEP 2: Details ─────────────────────────────────────── */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <p
                      className="font-accent text-[11px] uppercase tracking-[0.24em]"
                      style={{ color: accent }}
                    >
                      Step 2
                    </p>
                    <h2 className="mt-2 font-heading text-2xl text-charcoal md:text-3xl">
                      Your details
                    </h2>
                    <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-stone-600">
                      Add the details we should use to confirm the appointment
                      and send any follow-up information.
                    </p>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block font-body text-sm font-medium text-charcoal">
                        First name *
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(event) => {
                          setFirstName(event.target.value);
                          setErrors((current) => ({
                            ...current,
                            firstName: "",
                          }));
                        }}
                        placeholder="Your first name"
                        style={inputStyle("firstName")}
                      />
                      {errors.firstName && (
                        <p className="mt-2 font-body text-sm text-red-500">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block font-body text-sm font-medium text-charcoal">
                        Last name *
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(event) => {
                          setLastName(event.target.value);
                          setErrors((current) => ({
                            ...current,
                            lastName: "",
                          }));
                        }}
                        placeholder="Your last name"
                        style={inputStyle("lastName")}
                      />
                      {errors.lastName && (
                        <p className="mt-2 font-body text-sm text-red-500">
                          {errors.lastName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block font-body text-sm font-medium text-charcoal">
                        Email address *
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          setErrors((current) => ({
                            ...current,
                            email: "",
                          }));
                        }}
                        placeholder="you@example.com"
                        style={inputStyle("email")}
                      />
                      {errors.email && (
                        <p className="mt-2 font-body text-sm text-red-500">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block font-body text-sm font-medium text-charcoal">
                        Mobile number *
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(event) => {
                          setPhone(event.target.value);
                          setErrors((current) => ({ ...current, phone: "" }));
                        }}
                        placeholder="+44 7700 000000"
                        style={inputStyle("phone")}
                      />
                      {errors.phone && (
                        <p className="mt-2 font-body text-sm text-red-500">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white">
                        <UserRound size={18} style={{ color: accentBg }} />
                      </div>
                      <p className="font-body text-sm leading-relaxed text-stone-600">
                        Your information is used only to confirm the appointment
                        and support the booking process with the clinic team.
                        {hold?.phone
                          ? ` Your time is already held and the code was sent to ${hold.phone}.`
                          : ""}
                      </p>
                    </div>
                  </div>

                  {errors.submit && (
                    <p className="text-center font-body text-sm text-red-500">
                      {errors.submit}
                    </p>
                  )}
                </div>
              )}

              {/* ── STEP 3: Verification ────────────────────────────────── */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <p
                      className="font-accent text-[11px] uppercase tracking-[0.24em]"
                      style={{ color: accent }}
                    >
                      Step 3
                    </p>
                    <h2 className="mt-2 font-heading text-2xl text-charcoal md:text-3xl">
                      Confirm your number
                    </h2>
                    <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-stone-600">
                      Your time is held while you confirm. Enter the six-digit
                      code we sent to your mobile number to complete the
                      booking.
                    </p>
                  </div>

                  <div className="space-y-5 rounded-[1.5rem] border border-stone-200 bg-stone-50 p-5">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white">
                        <CheckCircle2 size={18} style={{ color: accentBg }} />
                      </div>
                      <div>
                        <p className="font-body text-sm font-semibold text-charcoal">
                          Verification code sent
                        </p>
                        <p className="mt-1 font-body text-sm text-stone-500">
                          We sent a six-digit code to{" "}
                          <span className="font-medium text-charcoal">
                            {hold?.phone || phone}
                          </span>
                          .
                          {hold?.holdExpiresAt
                            ? ` Enter it by ${formatSlotTime(
                                hold.holdExpiresAt
                              )} to keep this time.`
                            : ""}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setOtpInput("");
                            setOtpError("");
                            setStep(2);
                          }}
                          className="mt-2 font-body text-xs underline text-stone-500 hover:text-charcoal"
                        >
                          Use different details
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-center gap-2 sm:gap-3">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          ref={otpRefs[index]}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={otpInput[index] || ""}
                          onChange={(event) => handleOtpChange(event, index)}
                          className="h-14 w-12 rounded-[1rem] border-2 text-center font-body text-xl font-semibold outline-none"
                          style={{
                            borderColor: otpError
                              ? "#ef4444"
                              : otpInput[index]
                              ? accent
                              : "#E7E5E4",
                            background: "#FFFFFF",
                          }}
                        />
                      ))}
                    </div>

                    {otpError && (
                      <p className="text-center font-body text-sm text-red-500">
                        {otpError}
                      </p>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={confirmOtp}
                        disabled={otpVerifying}
                        className="inline-flex h-12 flex-1 items-center justify-center rounded-full font-body text-sm font-medium disabled:opacity-50"
                        style={{ background: accentBg, color: accent }}
                      >
                        {otpVerifying
                          ? "Verifying\u2026"
                          : "Verify and continue"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOtpInput("");
                          setOtpError("");
                          setStep(2);
                        }}
                        className="inline-flex h-12 items-center justify-center rounded-full border border-stone-300 px-5 font-body text-sm font-medium text-charcoal transition-colors hover:bg-white"
                      >
                        Change details
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Navigation ──────────────────────────────────────────── */}
              <div className="mt-8 flex flex-col gap-3 border-t border-stone-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setStep((current) => Math.max(1, current - 1))}
                  disabled={step === 1}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-stone-300 px-5 font-body text-sm font-medium text-charcoal transition-colors hover:bg-stone-50 disabled:opacity-40"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                {step === 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (validateStep(1)) setStep(2);
                    }}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-body text-sm font-medium text-warm-white transition-opacity hover:opacity-90 disabled:opacity-40"
                    style={{ background: accentBg }}
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    onClick={createHold}
                    disabled={submitting}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-body text-sm font-medium text-warm-white transition-opacity hover:opacity-90 disabled:opacity-40"
                    style={{ background: accentBg }}
                  >
                    {submitting ? "Reserving\u2026" : "Continue"}
                    {!submitting && <ArrowRight size={16} />}
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="button"
                    onClick={confirmOtp}
                    disabled={otpVerifying}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-body text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: accent, color: accentBg }}
                  >
                    {otpVerifying ? "Confirming\u2026" : "Confirm booking"}
                  </button>
                )}
              </div>
            </div>

            {/* ── Sidebar ─────────────────────────────────────────────── */}
            <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-[1.75rem] border border-stone-200 bg-white p-6 shadow-[0_18px_40px_rgba(41,37,36,0.06)]">
                <p
                  className="font-accent text-[11px] uppercase tracking-[0.24em]"
                  style={{ color: accent }}
                >
                  Your booking
                </p>
                <div className="mt-5 space-y-4">
                  {[
                    {
                      label: "Service",
                      value: selectedServiceObj?.name || "Not selected yet",
                    },
                    {
                      label: "Date",
                      value: formatLongDate(date),
                    },
                    {
                      label: "Time",
                      value: selectedSlot
                        ? `${formatSlotTime(selectedSlot.startsAt)} \u2014 ${providerName}`
                        : "Not selected",
                    },
                  ].map((entry) => (
                    <div
                      key={entry.label}
                      className="border-b border-stone-200 pb-4 last:border-b-0 last:pb-0"
                    >
                      <p className="font-accent text-[10px] uppercase tracking-[0.22em] text-stone-500">
                        {entry.label}
                      </p>
                      <p className="mt-2 font-body text-sm font-medium leading-relaxed text-charcoal">
                        {entry.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-stone-200 bg-stone-50 p-6">
                <p
                  className="font-accent text-[11px] uppercase tracking-[0.24em]"
                  style={{ color: accent }}
                >
                  What happens next
                </p>
                <div className="mt-5 space-y-4">
                  {[
                    "Choose the most suitable consultation or treatment type.",
                    "Add your details so the clinic can confirm the appointment.",
                    "Enter the code we text you to confirm your booking.",
                  ].map((item, index) => (
                    <div key={item} className="flex gap-3">
                      <div
                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-body text-xs font-semibold"
                        style={{
                          background:
                            index + 1 <= step ? accentBg : "#ECE9E4",
                          color: index + 1 <= step ? accent : "#78716C",
                        }}
                      >
                        {index + 1}
                      </div>
                      <p className="font-body text-sm leading-relaxed text-stone-600">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
